import {
  getCheckIfUserLockedServerUrl,
  getFormDataServerUrl,
  getPreviewServerUrl,
  getSendOtpServerUrl,
  getUpdateFormDataServerUrl,
  getVerifyOtpServerUrl,
  MAX_SEND_OTP_ERROR,
  MAX_VERIFY_OTP_ERROR,
} from '../consts/consts';
import { getTranslation } from '../consts/translations';
import { FormMetadata } from '../types';
import { InputType } from '../utils/InputType';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { v4 as uuidv4 } from 'uuid';
import { getStoredUuidPathSegment } from '../utils/urlUtils';
import { base64EncodeUint8 } from '../utils/bas64Utils';

type ApiTranslation = ReturnType<typeof getTranslation>;

function cleanPhoneNumber(phoneNumber: string): string {
  let cleanedNumber = phoneNumber.replace(/\D/g, '');

  if (cleanedNumber.startsWith('972')) {
    cleanedNumber = cleanedNumber.slice(3);
  }
  if (!cleanedNumber.startsWith('0')) {
    cleanedNumber = '0' + cleanedNumber;
  }
  return cleanedNumber;
}

async function getClientUniqueIdentifier(): Promise<string> {
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId;
  } catch (error) {
    console.error('Failed to get client unique identifier:', error);
    return uuidv4();
  }
}

export const verifyPatientAndSendOtp = async (
  phoneOrEmail: string,
  inputType: InputType,
  sessionId: string,
  translation: ApiTranslation,
  language: string,
) => {
  if (inputType === InputType.PhoneNumber) {
    phoneOrEmail = cleanPhoneNumber(phoneOrEmail);
  }
  const uniqueIdentifier = await getClientUniqueIdentifier();
  const response = await fetch(`${getSendOtpServerUrl()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      phone_or_email: phoneOrEmail,
      input_type: inputType.valueOf(),
      session_id: sessionId,
      website_language: language,
      identifier: uniqueIdentifier,
      uuid: getStoredUuidPathSegment(),
    }),
  });
  if (!response.ok) {
    if (response.status === 403) {
      throw new Error(MAX_SEND_OTP_ERROR);
    }
    if (response.status === 401) {
      throw new Error(translation.unauthorized_sf_error);
    }
    if (response.status === 404) {
      throw new Error(translation.form_does_not_exist_error);
    }
    throw new Error(translation.general_sever_error);
  }
  return await response.json();
};

export const validateCode = async (
  phoneOrEmail: string,
  otp: string,
  inputType: InputType,
  sessionId: string,
  translation: ApiTranslation,
  language: string,
) => {
  if (inputType === InputType.PhoneNumber) {
    phoneOrEmail = cleanPhoneNumber(phoneOrEmail);
  }
  const uniqueIdentifier = await getClientUniqueIdentifier();
  const response = await fetch(`${getVerifyOtpServerUrl()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      phone_or_email: phoneOrEmail,
      otp: otp,
      input_type: inputType.valueOf(),
      session_id: sessionId,
      website_language: language,
      identifier: uniqueIdentifier,
      uuid: getStoredUuidPathSegment(),
    }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error(translation.invalid_code_message);
    }
    if (response.status === 403) {
      throw new Error(MAX_VERIFY_OTP_ERROR);
    }
    if (response.status === 404) {
      throw new Error(translation.form_does_not_exist_error);
    }
    throw new Error(translation.general_sever_error);
  }
  return await response.json();
};

export const checkLockStatus = async (translation: ApiTranslation) => {
  const uniqueIdentifier = await getClientUniqueIdentifier();
  const response = await fetch(`${getCheckIfUserLockedServerUrl()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      identifier: uniqueIdentifier,
    }),
  });

  if (!response.ok) {
    throw new Error(translation.general_sever_error);
  }
  return await response.json();
};

export type FetchFormDataResponse = {
  form_data: string;
  language?: string;
};

export const fetchFormData = async (
  translation: ApiTranslation,
  logout: () => void,
  uuid?: string,
): Promise<FetchFormDataResponse> => {
  const response = await fetch(`${getFormDataServerUrl()}/${uuid}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(translation.form_does_not_exist_error);
    }
    if (response.status === 401 || response.status === 403) {
      logout();
    }
    throw new Error(translation.general_sever_error);
  }
  return await response.json();
};

export class PreviewFormError extends Error {
  status: number;

  constructor(status: number) {
    super(`Preview form request failed with status ${status}`);
    this.status = status;
  }
}

export class FormValidationError extends Error {
  errorQuestionKey: string;

  constructor(message: string, errorQuestionKey: string) {
    super(message);
    this.errorQuestionKey = errorQuestionKey;
  }
}

export const fetchPreviewForm = async (
  linkId: string,
): Promise<{ form_data: string; language?: string }> => {
  const response = await fetch(`${getPreviewServerUrl()}/${linkId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new PreviewFormError(response.status);
  }
  return await response.json();
};

export const updateFormData = async (
  formData: FormMetadata,
  translation: ApiTranslation,
  logout: () => void,
  uuid?: string,
  isSubmit: boolean = false,
) => {
  const jsonString = JSON.stringify(formData);
  const encodedBytes = new TextEncoder().encode(jsonString);
  const encodedFormData = base64EncodeUint8(encodedBytes);

  const response = await fetch(`${getUpdateFormDataServerUrl()}/${uuid}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      form_data_base64: encodedFormData,
      submit: isSubmit,
    }),
  });

  const responseBody = await response.json();

  if (!response.ok) {
    if (response.status === 400 && responseBody?.error_question_key) {
      throw new FormValidationError(responseBody.message, responseBody.error_question_key);
    }

    if (response.status === 401 || response.status === 403) {
      logout();
    }

    throw new Error(translation.general_sever_error);
  }

  return responseBody;
};
